import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import api from "../api/client";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      setReady(true);
      return;
    }
    api
      .get("/user/me")
      .then((r) => setUser(r.data.user))
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setReady(true));
  }, []);

  const login = useCallback((token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
  }, []);

  const register = useCallback(async (form) => {
    const { data } = await api.post("/user/register", form);
    localStorage.setItem("token", data.token);
    const me = await api.get("/user/me");
    setUser(me.data.user);
    return me.data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setUser(null);
  }, []);

  /** Balance changes after every order and top-up. Cheap to re-read. */
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/user/me");
      setUser(data.user);
    } catch (e) {
      /* ignore */
    }
  }, []);

  return (
    <Ctx.Provider value={{ user, ready, login, register, logout, refreshUser }}>
      {children}
    </Ctx.Provider>
  );
}

const Splash = () => (
  <div className="center muted" style={{ padding: "20vh 0" }}>
    Loading…
  </div>
);

export function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Splash />;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

/**
 * A client-side admin check is convenience, not security. The server returns 404
 * on every /api/admin route for non-admins regardless of what this renders.
 */
export function RequireAdmin({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Splash />;
  if (!user || !user.isAdmin) return <Navigate to="/" replace />;
  return children;
}
